import React, { useState } from 'react';
import axios from 'axios';
import { 
  Building2, Users, Package, Receipt, Clock, Wifi, WifiOff, 
  CheckCircle2, XCircle, ArrowRight, Phone, Mail, Calendar,
  BarChart3, CreditCard, Truck, UserCheck, FileText, Smartphone,
  Shield, Zap, TrendingUp, Store, Coffee, UtensilsCrossed, 
  ShoppingBag, Stethoscope, ChevronDown, ChevronUp, Play,
  MessageCircle, Star, Award, Globe, X, Loader2, Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FOSLandingPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [demoForm, setDemoForm] = useState({ full_name: '', business_name: '', phone: '', email: '', outlets: '', message: '' });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  
  // Subscription modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionStep, setSubscriptionStep] = useState('details'); // details, payment, success
  const [isProcessing, setIsProcessing] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone: '',
    gst_number: ''
  });
  const [paymentResult, setPaymentResult] = useState(null);

  const handlePlanSelect = (plan) => {
    // Always subscribe to FOS_PRO - single plan system
    setSelectedPlan({
      ...plan,
      id: 'FOS_PRO'  // Ensure we always send FOS_PRO to backend
    });
    setSubscriptionStep('details');
    setShowSubscriptionModal(true);
  };

  const handleSubscriptionFormChange = (e) => {
    setSubscriptionForm({
      ...subscriptionForm,
      [e.target.name]: e.target.value
    });
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleProceedToPayment = async () => {
    // Validation helpers
    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const validatePhone = (phone) => /^[\+]?[0-9]{10,15}$/.test(phone.replace(/\s/g, ''));
    const validateGSTIN = (gstin) => !gstin || /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gstin);

    // Validate form
    const errors = [];
    
    if (!subscriptionForm.first_name?.trim()) errors.push('First name is required');
    if (!subscriptionForm.last_name?.trim()) errors.push('Last name is required');
    if (!subscriptionForm.company_name?.trim()) errors.push('Company name is required');
    
    if (!subscriptionForm.email?.trim()) {
      errors.push('Email is required');
    } else if (!validateEmail(subscriptionForm.email)) {
      errors.push('Invalid email format');
    }
    
    if (!subscriptionForm.phone?.trim()) {
      errors.push('Phone number is required');
    } else if (!validatePhone(subscriptionForm.phone)) {
      errors.push('Invalid phone number (min 10 digits)');
    }
    
    if (subscriptionForm.gst_number && !validateGSTIN(subscriptionForm.gst_number)) {
      errors.push('Invalid GSTIN format');
    }

    if (errors.length > 0) {
      alert(errors.join('\n'));
      return;
    }

    setIsProcessing(true);

    try {
      // Check availability first
      const availabilityCheck = await axios.post(`${API}/auth/check-availability`, {
        email: subscriptionForm.email,
        phone: subscriptionForm.phone
      });

      if (!availabilityCheck.data.available) {
        const availErrors = Object.values(availabilityCheck.data.errors).join('\n');
        alert(availErrors);
        setIsProcessing(false);
        return;
      }

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        alert('Failed to load payment gateway. Please try again.');
        setIsProcessing(false);
        return;
      }

      // Create order
      const orderResponse = await axios.post(`${API}/subscription/create-order`, {
        plan_id: selectedPlan.id,
        ...subscriptionForm,
        num_franchises: 1
      });

      if (!orderResponse.data.success) {
        alert(orderResponse.data.error || 'Failed to create order');
        setIsProcessing(false);
        return;
      }

      const { order_id, amount, key_id } = orderResponse.data;

      // Open Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: 'INR',
        name: 'FOS - Franchise Operating System',
        description: `${selectedPlan.name} Subscription`,
        order_id: order_id,
        handler: async function (response) {
          // Verify payment
          try {
            const verifyResponse = await axios.post(`${API}/subscription/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: selectedPlan.id,
              ...subscriptionForm
            });

            if (verifyResponse.data.success) {
              setPaymentResult({
                success: true,
                client_id: verifyResponse.data.client_id,
                email: verifyResponse.data.email
              });
              setSubscriptionStep('success');
            } else {
              alert('Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            alert('Payment verification failed. Please contact support.');
          }
          setIsProcessing(false);
        },
        prefill: {
          name: `${subscriptionForm.first_name} ${subscriptionForm.last_name}`,
          email: subscriptionForm.email,
          contact: subscriptionForm.phone
        },
        theme: {
          color: '#3E2723'
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Payment error:', error);
      alert(error.response?.data?.detail || 'An error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!demoForm.full_name.trim() || !demoForm.phone.trim()) return;
    setDemoSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/demo-request`, demoForm);
      setShowDemoForm(false);
      setDemoForm({ full_name: '', business_name: '', phone: '', email: '', outlets: '', message: '' });
      alert('Thank you! We will contact you within 24 hours.');
    } catch {
      alert('Something went wrong. Please try again.');
    } finally {
      setDemoSubmitting(false);
    }
  };


  const painPoints = [
    { icon: <XCircle className="w-5 h-5" />, text: "No real-time visibility across your outlets" },
    { icon: <XCircle className="w-5 h-5" />, text: "Daily reports stitched together on WhatsApp and Excel" },
    { icon: <XCircle className="w-5 h-5" />, text: "Credit customers and ledger disputes eating your margins" },
    { icon: <XCircle className="w-5 h-5" />, text: "Staff attendance tracked on trust, not systems" },
    { icon: <XCircle className="w-5 h-5" />, text: "Supply chain managed through phone calls and memory" },
    { icon: <XCircle className="w-5 h-5" />, text: "Every outlet running its own version of your business" },
  ];

  const features = [
    {
      icon: <Receipt className="w-8 h-8" />,
      title: "Billing & Point of Sale",
      description: "Fast, accurate billing with cash, credit, and ledger support. Thermal printer and PDF invoices built in.",
      result: "Faster checkout. Cleaner books.",
      color: "from-amber-500 to-orange-500"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Centralized Command Center",
      description: "One dashboard to see every outlet's sales, performance, and health. Compare locations instantly.",
      result: "No blind spots. No surprises.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: "Ledger & Credit Management",
      description: "Party-wise balances with automatic debit and credit tracking. Invoices delivered via WhatsApp with PDF.",
      result: "Zero disputes. Faster collections.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Supply Chain & Ordering",
      description: "Outlets place digital orders. HQ tracks payment, delivery, and fulfillment from one screen.",
      result: "Clean supply chain. No confusion.",
      color: "from-purple-500 to-violet-500"
    },
    {
      icon: <UserCheck className="w-8 h-8" />,
      title: "Staff & Attendance",
      description: "Face-recognition based attendance. Accurate work hours, discipline, and accountability at every location.",
      result: "No proxy. No guesswork.",
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Expense Intelligence",
      description: "Daily expense entry with category-wise breakdowns. Track every rupee spent across every outlet.",
      result: "Plug leakage before it scales.",
      color: "from-indigo-500 to-blue-500"
    },
    {
      icon: <WifiOff className="w-8 h-8" />,
      title: "Offline-First Architecture",
      description: "Works without internet. Auto-syncs when back online. Runs on mobile, tablet, and desktop.",
      result: "Your business never stops.",
      color: "from-slate-600 to-slate-800"
    },
  ];

  const comparisonData = [
    { feature: "Designed for", normal: "Single stores", fos: "One to many outlets" },
    { feature: "Central control", normal: "No HQ dashboard", fos: "Full HQ visibility" },
    { feature: "Connectivity", normal: "Online only", fos: "Offline-first" },
    { feature: "Staff management", normal: "Not included", fos: "Attendance & payroll-ready" },
    { feature: "Supply chain", normal: "Not available", fos: "Built-in ordering system" },
    { feature: "Scalability", normal: "Add-ons & workarounds", fos: "Scale-ready by design" },
  ];

  const pricingPlans = [
    {
      id: "FOS_PRO",
      name: "FOS Pro",
      price: "499",
      regularPrice: "999",
      period: "/ outlet / month + GST",
      badge: "Launch Offer",
      offerText: "Valid for first 100 registrations",
      description: "Complete franchise management solution with all features",
      features: [
        "Centralized Menu Management",
        "Inventory Management",
        "Billing Service with Offline Support",
        "Reports & Analytics",
        "Expense Tracker",
        "WhatsApp Notifications",
        "Ledger Management",
        "Raw Materials Ordering",
        "Staff Management",
        "Attendance System",
        "Dedicated Support",
        "Unlimited Franchise Outlets"
      ],
      notIncluded: [],
      popular: true,
      color: "border-amber-500",
      buttonColor: "bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
    }
  ];

  const targetAudience = [
    { icon: <Coffee className="w-6 h-6" />, text: "Cafe, tea, coffee & juice chains", included: true },
    { icon: <UtensilsCrossed className="w-6 h-6" />, text: "Restaurants & food franchise brands", included: true },
    { icon: <Store className="w-6 h-6" />, text: "Retail stores & multi-outlet businesses", included: true },
    { icon: <ShoppingBag className="w-6 h-6" />, text: "Cloud kitchens & delivery brands", included: true },
    { icon: <Stethoscope className="w-6 h-6" />, text: "Clinics, salons & service chains", included: true },
    { icon: <TrendingUp className="w-6 h-6" />, text: "Any business planning to open outlet #2 and beyond", included: true },
  ];

  const faqs = [
    {
      question: "How long does it take to set up FOS?",
      answer: "Most businesses are up and running within 24-48 hours. Our team handles the complete setup including menu configuration, staff training, and data migration."
    },
    {
      question: "Does FOS work offline?",
      answer: "Yes. FOS is built offline-first. Your outlets can continue billing even without internet. All data syncs automatically when connectivity is restored."
    },
    {
      question: "I only have one outlet. Can I still use FOS?",
      answer: "Absolutely. FOS works for single outlets just as well. The difference is — when you open your second, third, or fiftieth location, you won't need to change your system. You'll already be running on one."
    },
    {
      question: "Can I add more outlets later?",
      answer: "Yes. FOS scales with you. Add new outlets anytime with a simple setup process. No complex migrations or data transfers needed."
    },
    {
      question: "What about my existing data?",
      answer: "We help you migrate existing data from Excel, Google Sheets, or other systems. Historical orders, ledger balances, and customer data can be imported."
    },
    {
      question: "Is there a free trial?",
      answer: "We offer a 7-day pilot program where you can test FOS with one outlet before committing. Book a demo to learn more."
    },
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Set Up Your HQ",
      description: "Configure your brand, menus, pricing, raw materials, staff, and reporting — all from one central dashboard.",
      icon: <Building2 className="w-10 h-10" />
    },
    {
      step: "02",
      title: "Connect Your Outlets",
      description: "Each location runs billing, ledger accounts, staff attendance, expenses, and ordering through a secure app.",
      icon: <Store className="w-10 h-10" />
    },
    {
      step: "03",
      title: "See Everything. In Real Time.",
      description: "Sales, attendance, expenses, supply orders — all synced to HQ automatically. No calls. No follow-ups.",
      icon: <BarChart3 className="w-10 h-10" />
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="/fos-logo.png" 
                alt="FOS - Franchise Operating System" 
                className="h-12 sm:h-14 w-auto object-contain"
              />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Features</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 text-sm font-medium">How it Works</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Pricing</a>
              <a href="#faq" className="text-slate-600 hover:text-slate-900 text-sm font-medium">FAQ</a>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowDemoForm(true)}
                className="hidden sm:block px-4 py-2 text-sm font-medium text-amber-600 hover:text-amber-700"
              >
                Request Demo
              </button>
              <button className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-500/25">
                Start Free Pilot
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-amber-50/50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full text-amber-700 text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              The Operating System for Operational Businesses
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              One System to Run{' '}
              <span className="text-amber-600">
                Every Outlet You Own
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
              Billing, inventory, staff, expenses, supply chain, and reporting — centralized. Whether you run 1 outlet or 100, FOS is the system that scales with you.
            </p>
            <p className="text-xl font-semibold text-slate-800 mb-10">
              Start with one outlet. Scale to many — without changing your system.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => setShowDemoForm(true)}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-xl shadow-amber-500/30 flex items-center justify-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                Request Free Demo
              </button>
              <button className="w-full sm:w-auto px-8 py-4 bg-white text-slate-800 rounded-xl text-lg font-semibold border-2 border-slate-200 hover:border-amber-500 hover:text-amber-600 transition-all flex items-center justify-center gap-2">
                <Play className="w-5 h-5" />
                Start 7-Day Pilot
              </button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-500" />
                Centralized Control
              </span>
              <span className="flex items-center gap-2">
                <WifiOff className="w-4 h-4 text-blue-500" />
                Offline Ready
              </span>
              <span className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-purple-500" />
                Any Device
              </span>
              <span className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-amber-500" />
                Scale Without Limits
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Operational Businesses Don&apos;t Fail Because of Demand
            </h2>
            <p className="text-xl text-amber-400 font-medium">
              They Fail Because of Broken Systems
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {painPoints.map((pain, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-slate-800/50 rounded-xl border border-slate-700">
                <div className="text-red-400 flex-shrink-0 mt-0.5">
                  {pain.icon}
                </div>
                <p className="text-slate-300">{pain.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <div className="inline-block px-8 py-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl">
              <p className="text-xl text-white font-semibold">
                FOS fixes this at the <span className="text-amber-400">system level</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Introduction */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full text-amber-700 text-sm font-medium mb-6">
            <Award className="w-4 h-4" />
            The Solution
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            FOS — The Franchise Operating System
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-4">
            FOS is a centralized operating system that brings billing, staff, inventory, expenses, ledgers, and supply chain under one roof — across every outlet you run.
          </p>
          <p className="text-xl font-semibold text-amber-600">
            Not another billing app. A system built for control, visibility, and scale.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              How FOS Works
            </h2>
            <p className="text-lg text-slate-600">Three steps to operational clarity</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100 h-full">
                  <div className="text-6xl font-bold text-amber-100 mb-4">{step.step}</div>
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center text-white mb-6 -mt-12">
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600">{step.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-amber-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Everything Your Business Needs — In One System
            </h2>
            <p className="text-lg text-slate-600">Built for outcomes, not just features</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group bg-white rounded-2xl p-8 border border-slate-200 hover:border-amber-300 hover:shadow-xl transition-all duration-300">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 mb-4">{feature.description}</p>
                <div className="flex items-center gap-2 text-green-600 font-medium">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>{feature.result}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Is It For */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Built for Businesses That Move Fast
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Whether you&apos;re running your first outlet or your fiftieth, FOS gives you the operational backbone to grow without chaos.
              </p>
              <div className="space-y-4">
                {targetAudience.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600">
                      {item.icon}
                    </div>
                    <span className="text-slate-700 font-medium">{item.text}</span>
                    <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-900 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                <Globe className="w-6 h-6 text-amber-400" />
                Our Vision
              </h3>
              <p className="text-slate-300 text-lg mb-6">
                Every operational business in the world deserves a system — not spreadsheets, not WhatsApp groups, not guesswork.
              </p>
              <p className="text-slate-300 mb-6">
                FOS is building the infrastructure layer for businesses that serve customers from physical locations — starting with franchises, expanding to every operational business that wants to scale.
              </p>
              <div className="p-4 bg-slate-800 rounded-xl border border-amber-500/30">
                <p className="text-amber-400 font-semibold text-lg">
                  &ldquo;We&apos;re not building for a niche. We&apos;re building for a category.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              FOS vs. Traditional Billing Software
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left p-4 bg-slate-100 rounded-tl-xl font-semibold text-slate-700">Feature</th>
                  <th className="text-center p-4 bg-slate-100 font-semibold text-slate-500">Normal Billing Apps</th>
                  <th className="text-center p-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-tr-xl font-semibold text-white">FOS</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className="border-b border-slate-100">
                    <td className="p-4 font-medium text-slate-700">{row.feature}</td>
                    <td className="p-4 text-center text-slate-500">{row.normal}</td>
                    <td className="p-4 text-center bg-amber-50 font-medium text-amber-700">{row.fos}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Simple Pricing. All Features Included.
            </h2>
            <p className="text-lg text-slate-400">One plan. Everything you need. Pay per outlet.</p>
          </div>
          
          {/* Single FOS Pro Plan */}
          {pricingPlans.map((plan, index) => (
            <div 
              key={index} 
              className="relative bg-slate-800 rounded-2xl p-8 border-2 border-amber-500 shadow-lg shadow-amber-500/20"
            >
              {/* Launch Offer Badge */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full text-white text-sm font-bold">
                {plan.badge}
              </div>
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-slate-400 text-sm">{plan.description}</p>
              </div>
              
              {/* Pricing */}
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-slate-500 line-through text-xl">₹{plan.regularPrice}</span>
                  <span className="text-5xl font-bold text-white">₹{plan.price}</span>
                </div>
                <span className="text-slate-400 text-sm">{plan.period}</span>
                <p className="text-amber-400 text-sm mt-2 font-medium">{plan.offerText}</p>
              </div>
              
              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {plan.features.map((feature, fIndex) => (
                  <div key={fIndex} className="flex items-start gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={() => handlePlanSelect(plan)}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${plan.buttonColor} text-white`}
              >
                Get Started Now
              </button>
            </div>
          ))}
          
          <p className="text-center text-slate-500 mt-8 text-sm">
            HQ Admin Portal included. Add unlimited outlets as you grow.
          </p>
        </div>
      </section>

      {/* Subscription Modal */}
      {showSubscriptionModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                  {subscriptionStep === 'success' ? '🎉 Welcome to FOS!' : `Subscribe to ${selectedPlan.name}`}
                </h3>
                {subscriptionStep !== 'success' && (
                  <div className="mt-1">
                    <span className="text-slate-400 line-through text-sm mr-2">₹{selectedPlan.regularPrice}</span>
                    <span className="text-amber-600 font-bold">₹{selectedPlan.price}/month per outlet</span>
                    <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Launch Offer</span>
                  </div>
                )}
              </div>
              <button 
                onClick={() => {
                  setShowSubscriptionModal(false);
                  setSubscriptionStep('details');
                  setPaymentResult(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-full"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              {subscriptionStep === 'details' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        value={subscriptionForm.first_name}
                        onChange={handleSubscriptionFormChange}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        value={subscriptionForm.last_name}
                        onChange={handleSubscriptionFormChange}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Company Name *</label>
                    <input
                      type="text"
                      name="company_name"
                      value={subscriptionForm.company_name}
                      onChange={handleSubscriptionFormChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="Your franchise brand name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={subscriptionForm.email}
                      onChange={handleSubscriptionFormChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="you@company.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={subscriptionForm.phone}
                      onChange={handleSubscriptionFormChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">GST Number (Optional)</label>
                    <input
                      type="text"
                      name="gst_number"
                      value={subscriptionForm.gst_number}
                      onChange={handleSubscriptionFormChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="For GST invoice"
                    />
                    <p className="text-xs text-slate-500 mt-1">Provide GST number if you need a GST invoice</p>
                  </div>
                  
                  {/* Summary */}
                  <div className="bg-slate-50 rounded-xl p-4 mt-6">
                    <h4 className="font-semibold text-slate-900 mb-2">Order Summary</h4>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{selectedPlan.name}</span>
                      <span className="font-medium">₹{selectedPlan.price}/month</span>
                    </div>
                    <div className="border-t border-slate-200 mt-2 pt-2 flex justify-between font-semibold">
                      <span>Total Due Today</span>
                      <span className="text-amber-600">₹{selectedPlan.price}</span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={handleProceedToPayment}
                    disabled={isProcessing}
                    className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Proceed to Payment
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                  
                  <p className="text-xs text-center text-slate-500">
                    By proceeding, you agree to our Terms of Service and Privacy Policy.
                    <br />Payments secured by Razorpay.
                  </p>
                </div>
              )}
              
              {subscriptionStep === 'success' && paymentResult && (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">You&apos;re All Set!</h3>
                  <p className="text-slate-600 mb-6">
                    Your {selectedPlan.name} subscription is now active.
                  </p>
                  
                  <div className="bg-slate-50 rounded-xl p-4 text-left mb-6">
                    <h4 className="font-semibold text-slate-900 mb-3">Your Login Credentials</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Client ID:</span>
                        <span className="font-mono font-medium">{paymentResult.client_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Email:</span>
                        <span className="font-medium">{paymentResult.email}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-200">
                        A temporary password has been sent to your email. Please check your inbox.
                      </p>
                    </div>
                  </div>
                  
                  <a 
                    href="https://billing.getfos.in/admin"
                    className="inline-flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-xl font-semibold transition-all"
                  >
                    Proceed to Dashboard
                    <ArrowRight className="w-5 h-5" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trust Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Built From Real Operations. Not a Lab.
          </h2>
          <p className="text-lg text-slate-600 mb-10 max-w-3xl mx-auto">
            FOS was born inside a real multi-outlet business. Every feature exists because an operator needed it — not because a product manager imagined it.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 bg-slate-50 rounded-2xl">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 mx-auto mb-4">
                <Award className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Battle-Tested</h3>
              <p className="text-slate-600 text-sm">Built and refined inside live operations, not simulations</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600 mx-auto mb-4">
                <Store className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Multi-Outlet Ready</h3>
              <p className="text-slate-600 text-sm">Proven across multiple outlets, built to handle hundreds</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mx-auto mb-4">
                <Globe className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Global Ambition</h3>
              <p className="text-slate-600 text-sm">Starting in India. Building for every operational business worldwide.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <span className="font-semibold text-slate-900">{faq.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6 text-slate-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-500 to-orange-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Your Business Deserves a System. Not a Workaround.
          </h2>
          <p className="text-xl text-amber-100 mb-10">
            Stop stitching operations together with WhatsApp, Excel, and phone calls. Put your business on a system that grows with you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={() => setShowDemoForm(true)}
              className="w-full sm:w-auto px-8 py-4 bg-white text-amber-600 rounded-xl text-lg font-semibold hover:bg-amber-50 transition-all shadow-xl flex items-center justify-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              Book Free Demo
            </button>
            <button className="w-full sm:w-auto px-8 py-4 bg-amber-600 text-white rounded-xl text-lg font-semibold border-2 border-amber-400 hover:bg-amber-700 transition-all flex items-center justify-center gap-2">
              <Phone className="w-5 h-5" />
              Talk to Founder
            </button>
            <button className="w-full sm:w-auto px-8 py-4 bg-transparent text-white rounded-xl text-lg font-semibold border-2 border-white/50 hover:bg-white/10 transition-all flex items-center justify-center gap-2">
              <Play className="w-5 h-5" />
              Start 7-Day Pilot
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">FOS</span>
                <p className="text-slate-500 text-sm">Franchise Operating System</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-slate-400 text-sm">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="mailto:support@fos.in" className="hover:text-white transition-colors flex items-center gap-2">
                <Mail className="w-4 h-4" />
                support@fos.in
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
            © {new Date().getFullYear()} FOS – Franchise Operating System. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Demo Form Modal */}
      {showDemoForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-slate-900">Request a Demo</h3>
              <button 
                onClick={() => setShowDemoForm(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form className="space-y-4" onSubmit={handleDemoSubmit}>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <input 
                  type="text" 
                  required
                  value={demoForm.full_name}
                  onChange={(e) => setDemoForm(p => ({ ...p, full_name: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Business Name</label>
                <input 
                  type="text" 
                  value={demoForm.business_name}
                  onChange={(e) => setDemoForm(p => ({ ...p, business_name: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Your business / brand name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number *</label>
                <input 
                  type="tel" 
                  required
                  value={demoForm.phone}
                  onChange={(e) => setDemoForm(p => ({ ...p, phone: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input 
                  type="email" 
                  value={demoForm.email}
                  onChange={(e) => setDemoForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Number of Outlets</label>
                <select 
                  value={demoForm.outlets}
                  onChange={(e) => setDemoForm(p => ({ ...p, outlets: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value="">Select</option>
                  <option value="1-5">1-5 outlets</option>
                  <option value="6-15">6-15 outlets</option>
                  <option value="16-50">16-50 outlets</option>
                  <option value="50+">50+ outlets</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message (Optional)</label>
                <textarea 
                  value={demoForm.message}
                  onChange={(e) => setDemoForm(p => ({ ...p, message: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Tell us about your requirements..."
                />
              </div>
              <button 
                type="submit"
                disabled={demoSubmitting || !demoForm.full_name.trim() || !demoForm.phone.trim()}
                className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50"
              >
                {demoSubmitting ? 'Submitting...' : 'Request Demo'}
              </button>
            </form>
            <p className="text-center text-slate-500 text-sm mt-4">
              We&apos;ll contact you within 24 hours
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FOSLandingPage;
